<template>
  <div v-if="isLoggedIn && mostRecentOrder" class="recent-order-reorder mt20">
    <h4 class="cl-accent align-center">{{ $t('Order again?') }}</h4>
    <p class="recent-order-summary align-center">
      {{ $t('Your recent order from') }} <strong>{{ mostRecentOrder.partner ? mostRecentOrder.partner.name : mostRecentOrder.partner_code }}</strong>
    </p>
    <bounce-loader v-if="loading" />
    <template v-else-if="reorderItems">
      <order-slider
        v-if="reorderItems.available.length"
        :config="sliderConfig"
        :items="reorderItems.available"
        :slides-page="slidesPage"
        :order-id="mostRecentOrder.id"
        :show-add-to-cart="true"
      />
      <div class="reorder-all-wrapper button-centered">
        <button-full class="bg-cl-korzyna-green checkout-link" @click.native="openReorderModal">
          {{ $t('Add all to cart') }}
        </button-full>
      </div>
      <reorder-list
        :order-id="mostRecentOrder.id"
        :order="mostRecentOrder"
        :products="{ available: reorderItems.available, notAvailable: reorderItems.notAvailable }"
        :count="reorderItems.available.length + reorderItems.notAvailable.length"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Logger } from '@vue-storefront/core/lib/logger'

const OrderSlider = () => import(/* webpackChunkName: "vsf-order-slider" */ 'theme/components/core/blocks/MyAccount/OrderSlider');
const ReorderList = () => import(/* webpackChunkName: "vsf-reorder-list" */ 'theme/components/theme/blocks/Order/ReorderList.vue');
const ButtonFull = () => import(/* webpackChunkName: "vsf-button-full" */ 'theme/components/theme/ButtonFull');
const BounceLoader = () => import(/* webpackChunkName: "vsf-bounce-loader" */ 'theme/components/theme/blocks/Home/Preloaders/BounceLoader');

/**
 * Cart-embedded reorder prompt (K2-2786) - shown when the cart is empty so a
 * returning, logged-in customer can reorder their last order (or add single
 * items from it) without leaving the cart to dig through order history.
 */
export default {
  name: 'RecentOrderReorder',
  components: {
    OrderSlider,
    ReorderList,
    ButtonFull,
    BounceLoader
  },
  data () {
    return {
      loading: false,
      reorderItems: null,
      sliderConfig: {
        arrows: false,
        infinite: false,
        draggable: false,
        cssEase: 'ease-out',
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 4,
        perPage: 3,
        autoScaleSlider: true,
        responsive: [
          { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 4, arrows: false } },
          { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 2, arrows: false } },
          { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 2, arrows: false } }
        ]
      },
      slidesPage: {
        desktop: 4,
        tablet: 4,
        mobileM: 2,
        mobileS: 2
      }
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'user/isLoggedIn',
      ordersHistory: 'user/getOrdersHistory',
      currentLocation: 'locations/getActiveLocation'
    }),
    mostRecentOrder () {
      if (!this.ordersHistory || !this.ordersHistory.length) {
        return null
      }
      return [...this.ordersHistory].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
    }
  },
  watch: {
    // isLoggedIn can still be false at mount time right after a page refresh - token/user
    // restoration from the cookie is async and may not have resolved yet. A one-shot mounted()
    // check would silently miss it forever since nothing else re-triggers this. This only kicks
    // off the orders-history load; it deliberately doesn't act on the result itself (see the
    // mostRecentOrder.id watcher below) - `user/getOrdersHistory` races a localForage cache read
    // against the network fetch and resolves on whichever answers first, so a stale/empty cache
    // can win and resolve this await before the real network response (and the order it actually
    // contains) has landed.
    isLoggedIn: {
      immediate: true,
      handler (isLoggedIn) {
        if (!isLoggedIn || this.ordersHistory.length) {
          return
        }
        this.loadOrdersHistory({
          refresh: true,
          useCache: true,
          page: 1,
          oldLocationCode: this.currentLocation.code,
          locationCode: this.currentLocation.code
        }).catch(error => {
          Logger.error('Cannot load order history for the cart reorder prompt', error)()
        })
      }
    },
    // Reacts to whichever of the cache/network answers turns out to be current, including a
    // later network response superseding an earlier stale cache hit above.
    'mostRecentOrder.id': {
      immediate: true,
      handler (orderId) {
        if (orderId) {
          this.loadReorderItems()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      loadOrdersHistory: 'user/getOrdersHistory',
      getOrderItemsForReorder: 'user/getOrderItemsForReorder'
    }),
    async loadReorderItems () {
      this.loading = true
      await this.getOrderItemsForReorder(this.mostRecentOrder.id)
        .then(resp => {
          this.reorderItems = { available: resp.available, notAvailable: resp.unavailable }
        })
        .catch(error => {
          Logger.error('Cannot load items for the cart reorder prompt', error)()
        })
        .finally(() => {
          this.loading = false
        })
    },
    openReorderModal () {
      this.$bus.$emit('modal-toggle', 'reorder-list' + this.mostRecentOrder.id)
    }
  }
}
</script>

<style scoped lang="scss">
.recent-order-reorder {
  padding: 0 15px;
}
.recent-order-summary {
  font-size: 14px;
  color: #666;
}
.reorder-all-wrapper {
  margin-top: 15px;
}
</style>
